import { SCHEMA_INVESTIGATOR_FILTERS_LOCALSTORAGE_KEY } from "./constants";

export interface StoredInvestigatorFilters {
  schemaName: string;
  // Id of the investigation the page displays (`payload.ID`); null while it
  // is an unsaved placeholder. Sidebar selection changes this without
  // navigating, so it can differ from `investigationIdFromUrl`.
  displayedInvestigationId: string | null;
  // Investigation id in the URL when the mirror was written, derived as
  // `params.investigationId ?? ?investigationId` — the reader must derive its
  // match key the same way.
  investigationIdFromUrl: string | null;
  smartGroupId: string | null;
  eventFilter: string | null;
  assetFilter: string | null;
}

// The investigator keeps filter edits in React state only, while Duck rebuilds
// its app_state from the URL + localStorage at prompt time (duck/ui/utils.ts).
// Mirroring the live filters here is what makes unsaved edits visible to Duck
// — the same mechanism the Dashboard and the old Investigator use.
export const saveInvestigatorFiltersToLocalStorage = (
  filters: StoredInvestigatorFilters
): void => {
  try {
    localStorage.setItem(
      SCHEMA_INVESTIGATOR_FILTERS_LOCALSTORAGE_KEY,
      JSON.stringify(filters)
    );
  } catch {
    // Storage can be unavailable (private browsing, quota). Drop the previous
    // entry if possible so a stale mirror isn't served instead of the URL
    // fallback.
    removeInvestigatorFiltersFromLocalStorage();
  }
};

export const removeInvestigatorFiltersFromLocalStorage = (): void => {
  try {
    localStorage.removeItem(SCHEMA_INVESTIGATOR_FILTERS_LOCALSTORAGE_KEY);
  } catch {
    // Storage is fully unavailable; the reader's JSON guard handles it.
  }
};

const isNullableString = (value: unknown): value is string | null =>
  value === null || typeof value === "string";

const isStoredInvestigatorFilters = (
  value: unknown
): value is StoredInvestigatorFilters => {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }

  const candidate = value as Partial<StoredInvestigatorFilters>;

  return (
    typeof candidate.schemaName === "string" &&
    isNullableString(candidate.displayedInvestigationId) &&
    isNullableString(candidate.investigationIdFromUrl) &&
    isNullableString(candidate.smartGroupId) &&
    isNullableString(candidate.eventFilter) &&
    isNullableString(candidate.assetFilter)
  );
};

export const loadInvestigatorFiltersFromLocalStorage = (
  investigationIdFromUrl: string | undefined,
  schemaName: string | undefined
): StoredInvestigatorFilters | undefined => {
  let stored: unknown;
  try {
    const raw = localStorage.getItem(
      SCHEMA_INVESTIGATOR_FILTERS_LOCALSTORAGE_KEY
    );
    if (!raw) return undefined;

    stored = JSON.parse(raw);
  } catch {
    return undefined;
  }

  if (!isStoredInvestigatorFilters(stored)) return undefined;

  // The stored blob may describe a previously visited page — only trust it
  // when it matches the URL context the reader sees. When the URL has no
  // investigation id (the ad-hoc investigator page), the schema is all there
  // is to match on.
  const matches = investigationIdFromUrl
    ? stored.investigationIdFromUrl === investigationIdFromUrl &&
      (!schemaName || stored.schemaName === schemaName)
    : stored.schemaName === schemaName;

  return matches ? stored : undefined;
};
